import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../../components/UI/Modal';
import Input from '../../components/UI/Input';
import { useSelector, useDispatch } from 'react-redux';
import { getAllItems, getRegions } from '../../actions';
import { roleOptions } from '../../store/localItems';
import UserConfirmDialog from './UserConfirmDialog';

const CODE_TTL_SECONDS = 300;

const isPhoneInputValid = (value) => {
    const raw = String(value || "").trim();

    if (!raw) return true;
    if (!/^[+\d\s()-]+$/.test(raw)) return false;

    const plusMatches = raw.match(/\+/g) || [];
    if (plusMatches.length > 1 || (plusMatches.length === 1 && !raw.startsWith("+"))) {
        return false;
    }

    const digits = raw.replace(/\D/g, "");

    if (raw.startsWith("+")) {
        return digits.length === 11 && digits.startsWith("7");
    }

    return digits.length === 10 ||
        (digits.length === 11 && (digits.startsWith("7") || digits.startsWith("8")));
};

const getSecureRandomIndex = (length) => {
    if (length <= 1) return 0;

    const cryptoApi = typeof window !== 'undefined' ? window.crypto : null;

    if (cryptoApi?.getRandomValues) {
        const value = new Uint32Array(1);
        cryptoApi.getRandomValues(value);
        return value[0] % length;
    }

    return Math.floor(Math.random() * length);
};

const generateStrongPassword = (len = 16) => {
    const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    const lower = 'abcdefghijkmnopqrstuvwxyz';
    const digits = '23456789';
    const symbols = '!@#$%&*+-_=.?';
    const all = upper + lower + digits + symbols;
    const pick = (pool) => pool[getSecureRandomIndex(pool.length)];
    const chars = [pick(upper), pick(lower), pick(digits), pick(symbols)];

    while (chars.length < len) chars.push(pick(all));

    for (let index = chars.length - 1; index > 0; index -= 1) {
        const swapIndex = getSecureRandomIndex(index + 1);
        [chars[index], chars[swapIndex]] = [chars[swapIndex], chars[index]];
    }

    return chars.join('');
};

const initialForm = {
    firstName: '',
    lastName: '',
    email: '',
    contactNumber: '',
    backupNumber: '',
    role: 'user',
    region: '',
    regionName: '',
    nameOrganization: '',
    addStandardSubs: false,
    subscriptions: [],
    useManualPassword: false,
    password: '',
    password2: '',
};

export default function CreateUserModal({ show, handleClose, onSubmit, onConfirm, onResend }) {
    const dispatch = useDispatch();

    const regionSlice = useSelector((state) => state.region);
    const regionsRaw = regionSlice?.regions || regionSlice?.items || regionSlice?.list || [];
    const { mainPages } = useSelector((state) => state.mainPage);

    const regionOptions = useMemo(() => {
        if (!Array.isArray(regionsRaw)) return [];

        return regionsRaw
            .map((region) => ({
                value: region?._id ?? region?.value ?? region?.id,
                name: region?.name ?? region?.label ?? region?.title ?? 'Без названия',
            }))
            .filter((option) => option.value && option.name);
    }, [regionsRaw]);

    const creatableRoleOptions = useMemo(() => (
        roleOptions.filter((option) => option.value !== 'superAdmin')
    ), []);

    const [form, setForm] = useState(initialForm);
    const [step, setStep] = useState('form');
    const [pendingUser, setPendingUser] = useState(null);
    const [tempPassword, setTempPassword] = useState('');
    const [code, setCode] = useState('');
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [busy, setBusy] = useState(false);
    const [showPwd, setShowPwd] = useState(false);
    const [showPwd2, setShowPwd2] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(0);
    const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);

    useEffect(() => {
        dispatch(getRegions());
        dispatch(getAllItems('mainPage', '_id,name'));
    }, [dispatch]);

    useEffect(() => {
        if (!show) return;

        setForm(initialForm);
        setStep('form');
        setPendingUser(null);
        setTempPassword('');
        setCode('');
        setError('');
        setMessage('');
        setBusy(false);
        setShowPwd(false);
        setShowPwd2(false);
        setSecondsLeft(0);
        setCloseConfirmOpen(false);
    }, [show]);

    useEffect(() => {
        if (!show || secondsLeft <= 0) return undefined;

        const timer = setInterval(() => {
            setSecondsLeft((current) => Math.max(current - 1, 0));
        }, 1000);

        return () => clearInterval(timer);
    }, [show, secondsLeft]);

    useEffect(() => {
        if (!form.addStandardSubs) return;
        if (!Array.isArray(mainPages) || mainPages.length === 0) return;

        setForm((current) => ({
            ...current,
            subscriptions: mainPages.map((mainPage) => ({
                mainPage: mainPage._id,
                isCompleted: true,
            })),
        }));
    }, [form.addStandardSubs, mainPages]);

    const onChange = (name, value) => {
        setForm((current) => ({ ...current, [name]: value }));
        setError('');
        setMessage('');
    };

    const onChangeRegion = (value) => {
        const option = regionOptions.find((item) => String(item.value) === String(value));

        setForm((current) => ({
            ...current,
            region: value,
            regionName: option?.name || '',
        }));
        setError('');
    };

    const isChecked = (id) => form.subscriptions.some((item) => (
        String(item.mainPage) === String(id) && item.isCompleted
    ));

    const toggleSub = (id) => {
        setForm((current) => {
            const exists = current.subscriptions.find((item) => String(item.mainPage) === String(id));
            const subscriptions = exists
                ? current.subscriptions.map((item) => (
                    String(item.mainPage) === String(id)
                        ? { ...item, isCompleted: !item.isCompleted }
                        : item
                ))
                : [...current.subscriptions, { mainPage: id, isCompleted: true }];

            return { ...current, subscriptions };
        });
    };

    const emailOk = /^\S+@\S+\.\S+$/.test(form.email.trim());
    const passwordOk = !form.useManualPassword || (
        form.password.length >= 6 && form.password === form.password2
    );
    const requiredOk = (
        form.firstName.trim().length >= 3 &&
        form.lastName.trim().length >= 3 &&
        emailOk &&
        !!form.role &&
        !!form.region &&
        !!form.regionName &&
        passwordOk
    );

    const formDirty = JSON.stringify(form) !== JSON.stringify(initialForm);
    const closeNeedsConfirm = step === 'verify' || (step === 'form' && formDirty);

    useEffect(() => {
        if (!show || !closeNeedsConfirm) return undefined;

        const beforeUnload = (event) => {
            event.preventDefault();
            event.returnValue = '';
        };

        window.addEventListener('beforeunload', beforeUnload);
        return () => window.removeEventListener('beforeunload', beforeUnload);
    }, [show, closeNeedsConfirm]);

    const close = () => {
        if (busy) return;

        if (closeNeedsConfirm && step !== 'done') {
            setCloseConfirmOpen(true);
            return;
        }

        handleClose?.();
    };

    const submit = async () => {
        if (busy) return;

        if (step === 'form') {
            if (!requiredOk) {
                setError('Проверьте обязательные поля формы.');
                return;
            }

            if (!isPhoneInputValid(form.contactNumber)) {
                setError('Основной телефон укажите в формате +7 999 123-45-67.');
                return;
            }

            if (!isPhoneInputValid(form.backupNumber)) {
                setError('Резервный телефон укажите в формате +7 999 123-45-67.');
                return;
            }

            setBusy(true);
            setError('');
            setMessage('');

            const result = await onSubmit?.({
                ...form,
                password: form.useManualPassword ? form.password : undefined,
            });

            setBusy(false);

            if (!result?.ok) {
                setError(result?.message || 'Ошибка создания пользователя');
                return;
            }

            setPendingUser({
                userId: result.userId || result.user?._id,
                email: result.email || result.user?.email || form.email,
            });
            setTempPassword(result.tempPassword || form.password || '');
            setSecondsLeft(Number(result.secondsLeft || CODE_TTL_SECONDS));
            setStep('verify');
            setMessage(result.message || 'Код подтверждения отправлен на email.');
            return;
        }

        if (step === 'verify') {
            if (!/^\d{6}$/.test(code.trim())) {
                setError('Введите 6-значный код подтверждения.');
                return;
            }

            setBusy(true);
            setError('');

            const result = await onConfirm?.({
                userId: pendingUser?.userId,
                code: code.trim(),
            });

            setBusy(false);

            if (!result?.ok) {
                setError(result?.message || 'Ошибка подтверждения email');
                return;
            }

            setMessage(result.message || 'Профиль активирован.');
            setStep('done');
        }
    };

    const resend = async () => {
        if (!pendingUser?.userId || busy || secondsLeft > CODE_TTL_SECONDS - 60) return;

        setBusy(true);
        setError('');

        const result = await onResend?.(pendingUser.userId);

        setBusy(false);

        if (!result?.ok) {
            setError(result?.message || 'Не удалось отправить код повторно');
            if (result?.secondsLeft) setSecondsLeft(result.secondsLeft);
            return;
        }

        setSecondsLeft(CODE_TTL_SECONDS);
        setMessage(result.message || 'Новый код отправлен.');
    };

    const generatePassword = () => {
        const password = generateStrongPassword();

        setForm((current) => ({
            ...current,
            useManualPassword: true,
            password,
            password2: password,
        }));
        setShowPwd(true);
        setShowPwd2(true);
        setMessage('Пароль сгенерирован и подставлен в оба поля.');
    };

    const copyPassword = async () => {
        if (!tempPassword) return;

        try {
            await navigator.clipboard.writeText(tempPassword);
            setMessage('Пароль скопирован в буфер обмена.');
        } catch (_error) {
            setMessage('Скопируйте пароль вручную.');
        }
    };

    const minutes = String(Math.floor(secondsLeft / 60)).padStart(2, '0');
    const seconds = String(secondsLeft % 60).padStart(2, '0');

    return (
        <>
        <Modal
            show={show}
            handleClose={close}
            modalTitle={step === 'form'
                ? 'Создать пользователя'
                : step === 'verify'
                    ? 'Подтвердить email пользователя'
                    : 'Профиль активирован'}
            onSubmit={step === 'done' ? close : submit}
            disabledSubmit={busy || (step === 'form' && !requiredOk) || (step === 'verify' && !/^\d{6}$/.test(code.trim()))}
            confirmText={step === 'done' ? 'Готово' : step === 'verify' ? 'Подтвердить' : 'Создать и отправить код'}
            closeText="Отмена"
            preventOutsideClose
        >
            {step === 'form' ? (
                <>
                    <div className="adminCreateUser__notice">
                        Профиль создаёт только superAdmin. Пользователь будет неактивен, пока вы не введёте код из его email.
                    </div>

                    <div className="row">
                        <div className="col-md-6">
                            <Input label="Имя*" name="firstName" value={form.firstName} placeholder="Имя" onChange={(event) => onChange('firstName', event.target.value)} />
                        </div>
                        <div className="col-md-6">
                            <Input label="Фамилия*" name="lastName" value={form.lastName} placeholder="Фамилия" onChange={(event) => onChange('lastName', event.target.value)} />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-7">
                            <Input label="Email*" name="email" value={form.email} placeholder="mail@example.com" onChange={(event) => onChange('email', event.target.value)} />
                        </div>
                        <div className="col-md-5">
                            <Input label="Телефон" name="contactNumber" value={form.contactNumber} placeholder="+7 999 123-45-67" onChange={(event) => onChange('contactNumber', event.target.value)} />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6">
                            <Input
                                type="select"
                                label="Роль*"
                                name="role"
                                value={form.role}
                                onChange={(event) => onChange('role', event.target.value)}
                                options={creatableRoleOptions}
                            />
                        </div>
                        <div className="col-md-6">
                            <Input
                                type="select"
                                label="Регион*"
                                name="region"
                                value={form.region}
                                onChange={(event) => onChangeRegion(event.target.value)}
                                options={regionOptions}
                                placeholder="Выбрать регион"
                            />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6">
                            <Input label="Резервный телефон" name="backupNumber" value={form.backupNumber} placeholder="+7 999 123-45-67" onChange={(event) => onChange('backupNumber', event.target.value)} />
                        </div>
                        <div className="col-md-6">
                            <Input label="Организация" name="nameOrganization" value={form.nameOrganization} placeholder="Название организации" onChange={(event) => onChange('nameOrganization', event.target.value)} />
                        </div>
                    </div>

                    <div className="adminCreateUser__passwordToolbar">
                        <label>
                            <input type="checkbox" checked={form.useManualPassword} onChange={(event) => onChange('useManualPassword', event.target.checked)} />
                            <span>Задать пароль вручную</span>
                        </label>

                        <button type="button" onClick={generatePassword}>Сгенерировать пароль</button>
                    </div>

                    {form.useManualPassword ? (
                        <div className="row">
                            <div className="col-md-6">
                                <Input type={showPwd ? 'text' : 'password'} label="Пароль*" name="password" value={form.password} placeholder="Минимум 6 символов" onChange={(event) => onChange('password', event.target.value)} />
                                <label className="adminCreateUser__showPassword">
                                    <input type="checkbox" checked={showPwd} onChange={() => setShowPwd((value) => !value)} />
                                    <span>Показать</span>
                                </label>
                            </div>
                            <div className="col-md-6">
                                <Input type={showPwd2 ? 'text' : 'password'} label="Повтор пароля*" name="password2" value={form.password2} placeholder="Повторите пароль" onChange={(event) => onChange('password2', event.target.value)} />
                                <label className="adminCreateUser__showPassword">
                                    <input type="checkbox" checked={showPwd2} onChange={() => setShowPwd2((value) => !value)} />
                                    <span>Показать</span>
                                </label>
                            </div>
                        </div>
                    ) : (
                        <div className="adminCreateUser__hint">Если пароль не задан, BACKEND сгенерирует сильный временный пароль и покажет его один раз после создания.</div>
                    )}

                    <div className="adminCreateUser__subscriptions">
                        <label>
                            <input type="checkbox" checked={form.addStandardSubs} onChange={(event) => onChange('addStandardSubs', event.target.checked)} />
                            <span>Добавить стандартные подписки</span>
                        </label>

                        {!form.addStandardSubs && Array.isArray(mainPages) && mainPages.length ? (
                            <details>
                                <summary>Настроить подписки вручную</summary>
                                <div className="adminCreateUser__subscriptionsList">
                                    {mainPages.map((mainPage) => (
                                        <label key={mainPage._id}>
                                            <input type="checkbox" checked={isChecked(mainPage._id)} onChange={() => toggleSub(mainPage._id)} />
                                            <span>{mainPage.name || mainPage.title || mainPage.slug || mainPage._id}</span>
                                        </label>
                                    ))}
                                </div>
                            </details>
                        ) : null}
                    </div>
                </>
            ) : null}

            {step === 'verify' ? (
                <div className="adminCreateUser__verify">
                    <div className="adminCreateUser__notice is-success">
                        Профиль создан, но пока не активирован. Код отправлен на <b>{pendingUser?.email}</b>.
                    </div>

                    <label className="adminCreateUser__codeField">
                        <span>Код из email</span>
                        <input
                            type="text"
                            inputMode="numeric"
                            value={code}
                            maxLength={6}
                            placeholder="123456"
                            onChange={(event) => {
                                setCode(event.target.value.replace(/\D/g, '').slice(0, 6));
                                setError('');
                            }}
                        />
                    </label>

                    <div className="adminCreateUser__timer">
                        <span>Код действует</span>
                        <b>{minutes}:{seconds}</b>
                    </div>

                    <button type="button" className="adminCreateUser__resend" onClick={resend} disabled={busy || secondsLeft > CODE_TTL_SECONDS - 60}>
                        Отправить код повторно
                    </button>

                    {tempPassword ? (
                        <div className="adminCreateUser__passwordResult">
                            <span>Пароль пользователя</span>
                            <code>{tempPassword}</code>
                            <button type="button" onClick={copyPassword}>Копировать</button>
                        </div>
                    ) : null}
                </div>
            ) : null}

            {step === 'done' ? (
                <div className="adminCreateUser__done">
                    <div className="adminCreateUser__notice is-success">
                        Email подтверждён. Профиль активирован и пользователь может входить в систему в рамках своей роли.
                    </div>

                    {tempPassword ? (
                        <div className="adminCreateUser__passwordResult">
                            <span>Сохраните пароль перед закрытием</span>
                            <code>{tempPassword}</code>
                            <button type="button" onClick={copyPassword}>Копировать</button>
                        </div>
                    ) : null}
                </div>
            ) : null}

            {message ? <div className="adminCreateUser__message">{message}</div> : null}
            {error ? <div className="adminCreateUser__error">{error}</div> : null}
        </Modal>

        <UserConfirmDialog
            show={closeConfirmOpen}
            title={step === 'verify' ? 'Закрыть подтверждение пользователя?' : 'Закрыть без сохранения?'}
            text={step === 'verify'
                ? 'Профиль уже создан, но email ещё не подтверждён. Ты сможешь вернуться к подтверждению позже из карточки пользователя.'
                : 'В форме создания есть введённые данные. При закрытии они будут потеряны.'}
            dangerAction={{
                label: step === 'verify' ? 'Закрыть окно' : 'Закрыть без сохранения',
                onClick: () => {
                    setCloseConfirmOpen(false);
                    handleClose?.();
                },
            }}
            secondaryAction={{
                label: 'Остаться здесь',
                onClick: () => setCloseConfirmOpen(false),
            }}
        />
        </>
    );
}
